import { FCX, usePlatformFeatureFlag } from "@datassential/platform-ui-lib";
import Loader from "@components/Loader/Loader";
import React, { useCallback, useEffect } from "react";
import useQueryState from "@hooks/useQueryState";
import { useNavigate } from "react-router-dom";
import { Routes } from "@routes";
import { AI_SEARCH_PAGE_ROUTES, getSearchUrl } from "@core/routes";
import { useAiSearchPage } from "@apiHooks/useAiSearchPage";
import { createRecentSearches } from "@apiHooks/useRecentSearches";
import { AI_SEARCH_ROUTE_PARAM, FEATURE_AI_SEARCH, FEATURE_AI_SEARCH_DS_AI } from "@constants/general";
import { AISearchPage } from "@models";
import { useAiSearchSettingsContext } from "@vms/AiSearchSettingsContext/AiSearchSettingsContext";

/**
 * Landing stop for an AI search: holds the loader while the stream (or, with
 * FEATURE_AI_SEARCH_DS_AI off, a GetPage call) decides which page the question
 * belongs on, then hands off.
 *
 * The stream itself is owned by `AiSearchSettingsContextProvider` above the
 * router, so it keeps running across the navigation below — the destination page
 * picks up the same run's settings and answer rather than starting its own.
 */
const AiSearch: FCX = () => {
    const [query] = useQueryState(AI_SEARCH_ROUTE_PARAM, '');
    const navigate = useNavigate();
    const { page, hasFailed } = useAiSearchSettingsContext();
    const isAiSearchEnabled = usePlatformFeatureFlag(FEATURE_AI_SEARCH);
    const isDsAiSearchEnabled = usePlatformFeatureFlag(FEATURE_AI_SEARCH_DS_AI);
    const { getPage } = useAiSearchPage();

    const routeToPage = useCallback(
        (target: AISearchPage, text: string) => {
            const route = AI_SEARCH_PAGE_ROUTES[target];
            if (route) {
                navigate(getSearchUrl(target, route, text));
            }

            createRecentSearches(text);
        },
        [navigate],
    );

    useEffect(() => {
        if (!query.trim() || !isAiSearchEnabled) {
            navigate(Routes.Home);
            return;
        }

        if (!isDsAiSearchEnabled) {
            getPage(query).then(target => routeToPage(target, query));
        }
    }, [query, isAiSearchEnabled, isDsAiSearchEnabled, getPage, routeToPage, navigate]);

    useEffect(() => {
        if (isDsAiSearchEnabled && hasFailed) {
            navigate(Routes.Home);
        }
    }, [isDsAiSearchEnabled, hasFailed, navigate]);

    useEffect(() => {
        if (!isDsAiSearchEnabled || page === null || !query.trim()) return;

        routeToPage(page, query);
    }, [isDsAiSearchEnabled, page, query, routeToPage]);

    return (
        <Loader
            isShown
            noBg
        />
    );
};

export default AiSearch;
